import {useCapabilityUdoColumns} from "~/contexts/workspaceContext";
import GrowDiagonalIcon from "@atlaskit/icon/core/grow-diagonal";
import ShrinkDiagonalIcon from "@atlaskit/icon/core/shrink-diagonal";
import {useMediaQuery} from "@mantine/hooks";
import type {QueryClient} from "@tanstack/react-query";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import type {DispositionSelectionState, GenericDispositionSelectorHandle} from "~/components/dispositionSelector";
import {
    DispositionPanelFooterActions,
    getDispositionSaveDisabledReason,
    EventContainerDetailsDispositionPanel,
} from "~/components/eventContainerDetails/eventContainerDetailsDispositionPanel";
import {useEventContainerListDispositionSave} from "~/components/eventContainerDetails/useEventContainerListDispositionSave";
import {
    AsideFullscreenPanel,
    AsideSidebarPanel,
    FullscreenModal,
    SidebarModal,
} from "~/components/ottopilotDataViewer/rowEngagementPanel";
import {useUnsavedChangesGuard} from "~/hooks/useUnsavedChangesGuard";
import {isFormValueDirty} from "~/utilities/isFormValueDirty";
import {useUserDefinedObjectOptionsByTemplateId, useWorkspace} from "~/contexts/workspaceContext";
import {AnankeLoadingOverlay} from "~/components/anankeLoadingOverlay";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {OttopilotDispositionConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerDispositionConfig";
import type {OttopilotTriggerConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTriggerConfig";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";
import type {EventWithEventContainer, EventContainerWithEvents} from "~/utilities/apis/api";
import {
    getEventContainerDispositionColumnId,
    getEventContainerDispositionId,
    getEventContainerDispositionOptions,
} from "~/utilities/ottopilotSemanticColumns";

const MOBILE_ASIDE_BREAKPOINT = "(max-width: 47.9375em)";

export function EventContainerListDispositionSidePanel(props: {
    onClose: () => void;
    event: EventWithEventContainer;
    eventContainerTemplate: EventContainerTemplate;
    triggerConfiguration: OttopilotTriggerConfig | undefined;
    dispositionConfiguration: OttopilotDispositionConfig;
    dispositionUiLevels?: number;
    getLeadDisplayName: (event: EventWithEventContainer) => string;
    queryKeyPrefix: ReadonlyArray<string | null>;
    /** Runs after a successful save, before ottopilot data-viewer invalidation (e.g. Amaron kanban dataset). */
    invalidateQueriesBeforeDataViewer?: () => Promise<void>;
    /** Runs after the Event Container disposition is persisted; use for optimistic kanban cache updates. */
    onDispositionSaved?: (args: {eventContainerId: Uuid; dispositionId: Uuid}) => void | Promise<void>;
    /** When false, skips the default data-viewer invalidation (e.g. optimistic kanban already updated). */
    refetchDataViewerAfterSave?: boolean;
    invalidateDataViewerQueries?: (queryClient: QueryClient, queryKeyPrefix: ReadonlyArray<string | null>) => Promise<void>;
    onRefreshStateChange?: (isRefreshing: boolean) => void;
    remarksMaxLength?: number;
}) {
    const getAdditionalColumns = useCapabilityUdoColumns();
    const {
        onClose,
        event,
        eventContainerTemplate,
        triggerConfiguration,
        dispositionConfiguration,
        dispositionUiLevels,
        getLeadDisplayName,
        queryKeyPrefix,
        invalidateQueriesBeforeDataViewer,
        onDispositionSaved,
        refetchDataViewerAfterSave,
        invalidateDataViewerQueries,
        onRefreshStateChange,
    } = props;

    const {workspace} = useWorkspace();
    const userDefinedObjectOptionsByTemplateId = useUserDefinedObjectOptionsByTemplateId();
    const remarksColumnId = eventContainerTemplate.configuration.eventContainerDetailsPage.remarksColumnId;
    const dispositions = [...getEventContainerDispositionOptions(getAdditionalColumns, eventContainerTemplate, userDefinedObjectOptionsByTemplateId)];
    const dispositionColumnId = getEventContainerDispositionColumnId(eventContainerTemplate);
    const eventContainerDispositionId = getEventContainerDispositionId(event.eventContainer, eventContainerTemplate);
    const isMobile = useMediaQuery(MOBILE_ASIDE_BREAKPOINT, false, {getInitialValueInEffect: true});

    const dispositionSelectorRef = useRef<GenericDispositionSelectorHandle>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [tempDispositionId, setTempDispositionId] = useState<Uuid | null>(null);
    const [tempSuggestedStatus, setTempSuggestedStatus] = useState<Uuid | null>(null);
    const [tempRemarks, setTempRemarks] = useState<string | null>(null);
    const [triggerEvent, setTriggerEvent] = useState<{additionalColumnValues: Record<Uuid, unknown>} | null>(null);
    const [selectionState, setSelectionState] = useState<DispositionSelectionState>("completed");

    const eventContainerForSelector: EventContainerWithEvents = useMemo(
        () => ({
            ...event.eventContainer,
            events: [],
        }),
        [event.eventContainer],
    );

    useEffect(() => {
        setTempDispositionId(eventContainerDispositionId);
        setTempSuggestedStatus(null);
        if (remarksColumnId !== null) {
            const existing = event.eventContainer.additionalColumnValues[remarksColumnId];
            setTempRemarks(typeof existing === "string" ? existing : null);
        } else {
            setTempRemarks(null);
        }
        setTriggerEvent(null);
        setSelectionState("completed");
    }, [event.eventContainer.id, event.eventContainer.additionalColumnValues, eventContainerDispositionId, remarksColumnId]);

    const eventContainerWithDiff: EventContainerWithEvents = useMemo(() => {
        const additionalColumnValues = {...event.eventContainer.additionalColumnValues};
        if (remarksColumnId !== null && tempRemarks !== null) {
            additionalColumnValues[remarksColumnId] = tempRemarks;
        }
        additionalColumnValues[dispositionColumnId] = tempDispositionId ?? eventContainerDispositionId;
        return {
            ...event.eventContainer,
            events: [],
            additionalColumnValues,
        };
    }, [dispositionColumnId, event.eventContainer, eventContainerDispositionId, tempDispositionId, tempRemarks, remarksColumnId]);

    const {handleSave, isSaving} = useEventContainerListDispositionSave({
        event,
        triggerConfiguration,
        dispositionConfiguration,
        remarksColumnId,
        queryKeyPrefix,
        getLeadDisplayName,
        invalidateQueriesBeforeDataViewer,
        onDispositionSaved,
        refetchDataViewerAfterSave,
        invalidateDataViewerQueries,
        onRefreshStateChange,
        onClose,
    });

    const resetFormState = useCallback(() => {
        setTempDispositionId(eventContainerDispositionId);
        setTempSuggestedStatus(null);
        if (remarksColumnId !== null) {
            const existing = event.eventContainer.additionalColumnValues[remarksColumnId];
            setTempRemarks(typeof existing === "string" ? existing : null);
        } else {
            setTempRemarks(null);
        }
        setTriggerEvent(null);
        setSelectionState("completed");
        dispositionSelectorRef.current?.reset();
    }, [event.eventContainer, eventContainerDispositionId, remarksColumnId]);

    const handleCancel = useCallback(() => {
        if (isSaving === true) {
            return;
        }
        resetFormState();
        onClose();
    }, [isSaving, onClose, resetFormState]);

    // Guard against losing unsaved disposition edits on close / back / refresh.
    const initialRemarks =
        remarksColumnId !== null && typeof event.eventContainer.additionalColumnValues[remarksColumnId] === "string"
            ? (event.eventContainer.additionalColumnValues[remarksColumnId] as string)
            : null;
    const isDirty =
        selectionState === "inProgress" ||
        isFormValueDirty(
            {
                dispositionId: tempDispositionId,
                suggestedStatus: tempSuggestedStatus,
                remarks: tempRemarks,
                triggerEvent,
            },
            {
                dispositionId: eventContainerDispositionId,
                suggestedStatus: null,
                remarks: initialRemarks,
                triggerEvent: null,
            },
        );
    const {requestClose, modal} = useUnsavedChangesGuard({isDirty, onDiscard: handleCancel});

    const handleDispositionSave = useCallback(() => {
        if (tempDispositionId === null) {
            return;
        }
        handleSave({
            dispositionId: tempDispositionId,
            suggestedStatus: tempSuggestedStatus,
            remarks: tempRemarks,
            trigger: triggerEvent,
        });
    }, [handleSave, tempDispositionId, tempSuggestedStatus, tempRemarks, triggerEvent]);

    const saveDisabledReason = useMemo(
        () =>
            getDispositionSaveDisabledReason({
                eventContainerDispositionId: eventContainerDispositionId,
                selectionState: selectionState,
                tempDispositionId: tempDispositionId,
                triggerConfig: triggerConfiguration,
                triggerEvent: triggerEvent,
            }),
        [eventContainerDispositionId, selectionState, tempDispositionId, triggerConfiguration, triggerEvent],
    );

    const headerActions = (
        <>
            <AnankeIconButton
                type="button"
                appearance="subtle"
                icon={isFullscreen === true ? ShrinkDiagonalIcon : GrowDiagonalIcon}
                label={isFullscreen === true ? "Collapse" : "Expand"}
                isDisabled={isSaving === true}
                onClick={() => setIsFullscreen((current) => !current)}
            />
        </>
    );

    const body = (
        <div className="relative grid min-h-48 gap-ds-space-200 px-ds-space-250 py-ds-space-200">
            <AnankeLoadingOverlay
                visible={isSaving === true}
                message="Updating disposition..."
            />
            <EventContainerDetailsDispositionPanel
                dispositionSelectorRef={dispositionSelectorRef}
                eventContainer={eventContainerForSelector}
                eventContainerTemplate={eventContainerTemplate}
                workspaceId={workspace.id}
                dispositions={dispositions}
                dispositionConfig={dispositionConfiguration}
                triggerConfig={triggerConfiguration}
                dispositionUiLevels={dispositionUiLevels}
                tempDispositionId={tempDispositionId}
                setTempDispositionId={setTempDispositionId}
                setTempSuggestedStatus={setTempSuggestedStatus}
                triggerEvent={triggerEvent}
                setTriggerEvent={setTriggerEvent}
                remarksColumn={remarksColumnId}
                tempRemarks={tempRemarks}
                setTempRemarks={setTempRemarks}
                eventContainerWithDiff={eventContainerWithDiff}
                onDispositionCancel={() => requestClose()}
                onDispositionSave={handleDispositionSave}
                isDispositionSavePending={isSaving === true}
                isDispositionUpdateDisabled={false}
                hideActions={true}
                onSelectionStateChange={setSelectionState}
            />
        </div>
    );

    const footer = (
        <div className="flex h-full w-full min-w-0 items-center justify-end gap-2 px-ds-space-150">
            <DispositionPanelFooterActions
                saveLabel="Update"
                saveDisabledReason={saveDisabledReason}
                isSavePending={isSaving === true}
                onCancel={() => requestClose()}
                onSave={handleDispositionSave}
            />
        </div>
    );

    const panel =
        isMobile === true ? (
            isFullscreen === true ? (
                <FullscreenModal
                    headerLeft="Disposition"
                    headerActions={headerActions}
                    useStandardCloseButton={true}
                    closeDisabled={isSaving === true}
                    body={body}
                    footer={footer}
                    onClose={() => requestClose()}
                />
            ) : (
                <SidebarModal
                    headerLeft="Disposition"
                    headerActions={headerActions}
                    useStandardCloseButton={true}
                    closeDisabled={isSaving === true}
                    body={body}
                    footer={footer}
                    onClose={() => requestClose()}
                />
            )
        ) : isFullscreen === true ? (
            <AsideFullscreenPanel
                headerLeft="Disposition"
                headerActions={headerActions}
                useStandardCloseButton={true}
                closeDisabled={isSaving === true}
                onCloseHeader={() => requestClose()}
                body={body}
                footer={footer}
                bodyFillsViewport={true}
            />
        ) : (
            <AsideSidebarPanel
                headerLeft="Disposition"
                headerActions={headerActions}
                useStandardCloseButton={true}
                closeDisabled={isSaving === true}
                onCloseHeader={() => requestClose()}
                body={body}
                footer={footer}
            />
        );

    return (
        <>
            {panel}
            {modal}
        </>
    );
}
